import { Component, Inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Maintenance, MaintenanceMaterial, MaintenanceRecurrenceType, MaintenanceRegieLine } from '../../../../Models/maintenance.model';
import { Customer } from '../../../../Models/customer.model';
import { MaintenanceChecklist } from '../../../../Models/maintenance-checklist.model';
import { MaintenanceService } from '../../../../Services/maintenance.service';
import { jsPDF } from 'jspdf';
import { drawPdfHeader, drawSectionTitle } from '../pdf-branding.util';

export interface ReportDialogData {
  maintenance: Maintenance;
  customer: Customer | null;
  isPlanned: boolean;
  durationHours?: number | null;
  durationMinutes?: number | null;
  // Service call details
  workItems?: string[];
  description?: string;
  photos?: { dataUrl: string; name: string }[];
  signatureDataUrl?: string | null;
  customerSigneeName?: string;
  arrivalTime?: string | null;
  departureTime?: string | null;
  travelHours?: number | null;
  travelMinutes?: number | null;
  departureFromOffice?: string | null;
  returnToOffice?: string | null;
  initialSituation?: string;
  problems?: any[];
  techSigneeName?: string;
  techSignatureDataUrl?: string | null;
  regieLines?: MaintenanceRegieLine[];
}

export interface GeneratedReportPdf {
  fileName: string;
  pdfBase64: string;
}

export interface ReportDialogResult {
  generatedPdf?: GeneratedReportPdf;
}

@Component({
  selector: 'app-service-report-dialog',
  templateUrl: './service-report-dialog.component.html',
  styleUrls: ['./service-report-dialog.component.css']
})
export class ServiceReportDialogComponent implements AfterViewInit {
  @ViewChild('technicianCanvas') technicianCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('customerCanvas') set customerCanvasSetter(ref: ElementRef<HTMLCanvasElement>) {
    if (ref && ref !== this._customerCanvasRef) {
      this._customerCanvasRef = ref;
      setTimeout(() => {
        this.initCanvas(ref, 'cust');
        this.loadCustomerSignature();
      }, 50);
    }
  }
  private _customerCanvasRef: ElementRef<HTMLCanvasElement> | null = null;

  get customerCanvas(): ElementRef<HTMLCanvasElement> {
    return this._customerCanvasRef!;
  }

  maintenance: Maintenance;
  customer: Customer | null;
  isPlanned: boolean;
  durationHours: number;
  durationMinutes: number;
  travelHours: number;
  travelMinutes: number;

  // Service call data
  workItems: string[];
  description: string;
  scPhotos: { dataUrl: string; name: string }[];
  existingSignatureDataUrl: string | null;
  existingTechSignatureDataUrl: string | null;
  existingCustomerSigneeName: string;
  arrivalTime: string | null;
  departureTime: string | null;

  additionalNotes = '';
  technicianName = '';
  customerSigneeName = '';
  includeCustomerSignature = true;
  savingToServer = false;

  private techCtx: CanvasRenderingContext2D | null = null;
  private custCtx: CanvasRenderingContext2D | null = null;
  private techDrawing = false;
  private custDrawing = false;
  techSigned = false;
  custSigned = false;

  constructor(
    public dialogRef: MatDialogRef<ServiceReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportDialogData,
    private maintenanceService: MaintenanceService,
    private snackBar: MatSnackBar
  ) {
    this.maintenance = data.maintenance;
    this.customer = data.customer;
    this.isPlanned = data.isPlanned;
    this.durationHours = data.durationHours ?? 0;
    this.durationMinutes = data.durationMinutes ?? 0;
    this.travelHours = data.travelHours ?? 0;
    this.travelMinutes = data.travelMinutes ?? 0;
    this.technicianName = data.techSigneeName || data.maintenance.assignedUser?.userName || data.maintenance.assignedEmployee?.fullName || '';

    // Service call data
    this.workItems = data.workItems ?? [];
    this.description = data.description ?? '';
    this.scPhotos = data.photos ?? [];
    this.existingSignatureDataUrl = data.signatureDataUrl ?? null;
    this.existingTechSignatureDataUrl = data.techSignatureDataUrl ?? null;
    this.existingCustomerSigneeName = data.customerSigneeName ?? '';
    this.arrivalTime = data.arrivalTime ?? null;
    this.departureTime = data.departureTime ?? null;
    if (this.existingCustomerSigneeName) {
      this.customerSigneeName = this.existingCustomerSigneeName;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initCanvas(this.technicianCanvas, 'tech');
      this.loadTechnicianSignature();
    }, 100);
  }

  private initCanvas(ref: ElementRef<HTMLCanvasElement>, type: 'tech' | 'cust'): void {
    if (!ref) return;
    const canvas = ref.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (type === 'tech') this.techCtx = ctx;
    else this.custCtx = ctx;

    canvas.addEventListener('mousedown', (e) => this.startDraw(e, type));
    canvas.addEventListener('mousemove', (e) => this.draw(e, type));
    canvas.addEventListener('mouseup', () => this.endDraw(type));
    canvas.addEventListener('mouseleave', () => this.endDraw(type));

    // Touch support
    canvas.addEventListener('touchstart', (e) => { e.preventDefault(); this.startDrawTouch(e, type); });
    canvas.addEventListener('touchmove', (e) => { e.preventDefault(); this.drawTouch(e, type); });
    canvas.addEventListener('touchend', () => this.endDraw(type));
  }

  private getPos(e: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private getTouchPos(e: TouchEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }

  private startDraw(e: MouseEvent, type: 'tech' | 'cust'): void {
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    if (type === 'tech') this.techDrawing = true; else this.custDrawing = true;
    const pos = this.getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  private draw(e: MouseEvent, type: 'tech' | 'cust'): void {
    const drawing = type === 'tech' ? this.techDrawing : this.custDrawing;
    if (!drawing) return;
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    const pos = this.getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (type === 'tech') this.techSigned = true; else this.custSigned = true;
  }

  private startDrawTouch(e: TouchEvent, type: 'tech' | 'cust'): void {
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    if (type === 'tech') this.techDrawing = true; else this.custDrawing = true;
    const pos = this.getTouchPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  private drawTouch(e: TouchEvent, type: 'tech' | 'cust'): void {
    const drawing = type === 'tech' ? this.techDrawing : this.custDrawing;
    if (!drawing) return;
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    const pos = this.getTouchPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (type === 'tech') this.techSigned = true; else this.custSigned = true;
  }

  private endDraw(type: 'tech' | 'cust'): void {
    if (type === 'tech') this.techDrawing = false; else this.custDrawing = false;
  }

  clearSignature(type: 'tech' | 'cust'): void {
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (type === 'tech') this.techSigned = false; else this.custSigned = false;
  }

  get checklists(): MaintenanceChecklist[] {
    return this.maintenance.checklists ?? [];
  }

  get materials(): MaintenanceMaterial[] {
    return this.maintenance.materials ?? [];
  }

  get regieLines(): MaintenanceRegieLine[] {
    return this.maintenance.regieLines?.length
      ? this.maintenance.regieLines
      : this.data.regieLines ?? [];
  }

  get regieText(): string {
    if (this.regieLines.length === 0) return '–';
    return `${this.getRegieHoursTotal().toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h · ${this.getRegieTotal().toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}`;
  }

  getRegieTotal(): number {
    return this.regieLines.reduce((sum, line) => sum + ((Number(line.hours) || 0) * (Number(line.hourlyRate) || 0)), 0);
  }

  getRegieHoursTotal(): number {
    return this.regieLines.reduce((sum, line) => sum + (Number(line.hours) || 0), 0);
  }

  get totalItems(): number {
    return this.checklists.reduce((s, cl) => s + (cl.items?.length ?? 0), 0);
  }

  get completedItems(): number {
    return this.checklists.reduce((s, cl) => s + ((cl.items ?? []).filter((i: any) => i.isChecked).length), 0);
  }

  get durationText(): string {
    const h = this.durationHours || 0;
    const m = this.durationMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get travelTimeText(): string {
    const h = this.travelHours || 0;
    const m = this.travelMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  async generatePDF(): Promise<void> {
    // Save signatures before generating PDF
    this.saveSignaturesOnExport();
    this.persistSignaturesToMaintenance();
    try {
      const doc = new jsPDF();

      const pageWidth = doc.internal.pageSize.getWidth();
      let y = await drawPdfHeader(
        doc,
        this.isPlanned ? 'Wartungs-Report' : 'Einsatz-Report',
        `Erstellt am: ${new Date().toLocaleDateString('de-AT')}`
      );

      // Customer & Maintenance Info
      y = drawSectionTitle(doc, y, 'Auftragsinformationen');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const info = [
        ['Kunde:', this.customer?.name ?? '–'],
        ['Adresse:', this.customer?.address ?? '–'],
        ['Auftrag:', this.maintenance.title],
        ['Anlage:', this.maintenance.equipment?.name ?? '–'],
        ['Datum:', new Date(this.maintenance.scheduledDate).toLocaleDateString('de-AT')],
        ['Techniker:', this.technicianName || '–'],
      ];

      if (!this.isPlanned) {
        info.push(['Arbeitszeit:', this.durationText]);
        info.push(['Fahrzeit:', this.travelTimeText]);
        if (this.regieLines.length > 0) info.push(['Regie:', this.regieText]);
        if (this.arrivalTime) info.push(['Ankunft:', this.arrivalTime]);
        if (this.departureTime) info.push(['Abreise:', this.departureTime]);
      }

      for (const [label, value] of info) {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 14, y);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 55, y);
        y += 6;
      }
      y += 8;

      // Work Description & Items (Service Calls)
      if (!this.isPlanned && (this.description || this.workItems.length > 0)) {
        y = drawSectionTitle(doc, y, 'Durchgeführte Arbeiten');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        if (this.description) {
          const descLines = doc.splitTextToSize(this.description, pageWidth - 28);
          doc.text(descLines, 14, y);
          y += descLines.length * 5 + 4;
        }
        if (this.workItems.length > 0) {
          for (let i = 0; i < this.workItems.length; i++) {
            const itemText = `${i + 1}. ${this.workItems[i]}`;
            const itemLines = doc.splitTextToSize(itemText, pageWidth - 32);
            doc.text(itemLines, 18, y);
            y += itemLines.length * 5;
            if (y > 270) { doc.addPage(); y = 20; }
          }
        }
        y += 6;
      }

      // Checklists
      if (this.checklists.length > 0) {
        y = drawSectionTitle(doc, y, `Checklisten (${this.completedItems}/${this.totalItems} erledigt)`);

        doc.setFontSize(9);
        for (const cl of this.checklists) {
          doc.setFont('helvetica', 'bold');
          doc.text(cl.name || 'Checkliste', 14, y);
          y += 5;
          doc.setFont('helvetica', 'normal');
          for (const item of cl.items ?? []) {
            const symbol = item.isChecked ? '[x]' : '[ ]';
            doc.text(`  ${symbol} ${item.description}`, 18, y);
            y += 5;
            if (y > 270) { doc.addPage(); y = 20; }
          }
          y += 3;
        }
        y += 5;
      }

      // Materials
      if (this.regieLines.length > 0) {
        y = drawSectionTitle(doc, y, 'Regiestunden');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        for (const line of this.regieLines) {
          const total = (line.hours * line.hourlyRate).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
          doc.text(`${line.label}: ${line.hours.toLocaleString('de-AT')} h x ${line.hourlyRate.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}/h = ${total}`, 18, y);
          y += 5;
          if (y > 270) { doc.addPage(); y = 20; }
        }
        y += 5;
      }

      // Materials
      if (this.materials.length > 0) {
        y = drawSectionTitle(doc, y, 'Verwendetes Material');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        for (const mat of this.materials) {
          doc.text(`• ${mat.name} – ${mat.quantity} ${mat.unit}`, 18, y);
          y += 5;
          if (y > 270) { doc.addPage(); y = 20; }
        }
        y += 5;
      }

      // Photos (Service Calls)
      if (this.scPhotos.length > 0) {
        if (y > 180) { doc.addPage(); y = 20; }
        y = drawSectionTitle(doc, y, 'Fotodokumentation');
        y += 2;
        const imgW = 80;
        const imgH = 60;
        let x = 14;
        for (const photo of this.scPhotos) {
          if (y + imgH > 270) { doc.addPage(); y = 20; x = 14; }
          const photoDataUrl = await this.resolvePhotoDataUrl(photo.dataUrl);
          try {
            doc.addImage(photoDataUrl, 'JPEG', x, y, imgW, imgH);
          } catch {
            try { doc.addImage(photoDataUrl, 'PNG', x, y, imgW, imgH); } catch { /* skip */ }
          }
          if (x === 14) { x = pageWidth / 2 + 5; } else { x = 14; y += imgH + 8; }
        }
        if (x !== 14) y += imgH + 8;
        y += 6;
      }

      // Existing Customer Signature (from service call)
      if (this.includeCustomerSignature && this.existingSignatureDataUrl) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Kundenunterschrift (Einsatz)', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(this.existingCustomerSigneeName || '–', 14, y);
        y += 4;
        const eSigW = pageWidth - 28;
        const eSigH = 30;
        try {
          doc.addImage(this.existingSignatureDataUrl, 'PNG', 14, y, eSigW, eSigH);
          y += eSigH + 4;
        } catch { /* skip */ }
        doc.setDrawColor(200);
        doc.line(14, y, pageWidth - 14, y);
        y += 8;
      }

      // Additional notes
      if (this.additionalNotes.trim()) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Zusätzliche Anmerkungen', 14, y);
        y += 8;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(this.additionalNotes, pageWidth - 28);
        doc.text(lines, 14, y);
        y += lines.length * 5 + 8;
      }

      // Check if we need a new page for signatures
      if (y > 210) { doc.addPage(); y = 20; }

      // Signatures
      y += 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Unterschriften', 14, y);
      y += 12;

      const sigWidth = 70;
      const sigHeight = 30;

      // Technician signature
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Techniker:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(this.technicianName || '–', 14, y + 5);
      const techImg = this.techSigned
        ? this.technicianCanvas.nativeElement.toDataURL('image/png')
        : this.existingTechSignatureDataUrl;
      if (techImg) {
        doc.addImage(techImg, 'PNG', 14, y + 8, sigWidth, sigHeight);
      }
      doc.setDrawColor(200);
      doc.line(14, y + sigHeight + 12, 14 + sigWidth, y + sigHeight + 12);

      // Customer signature
      if (this.includeCustomerSignature) {
        doc.setFont('helvetica', 'bold');
        doc.text('Kunde:', pageWidth / 2 + 10, y);
        doc.setFont('helvetica', 'normal');
        doc.text(this.customerSigneeName || '–', pageWidth / 2 + 10, y + 5);
        if (this.custSigned) {
          const custImg = this.customerCanvas.nativeElement.toDataURL('image/png');
          doc.addImage(custImg, 'PNG', pageWidth / 2 + 10, y + 8, sigWidth, sigHeight);
        }
        doc.line(pageWidth / 2 + 10, y + sigHeight + 12, pageWidth / 2 + 10 + sigWidth, y + sigHeight + 12);
      }

      // Save
      const filename = `${this.isPlanned ? 'Wartung' : 'Einsatz'}_${this.customer?.name ?? 'Report'}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const pdfDataUri = doc.output('datauristring');
      const pdfBase64 = pdfDataUri.includes(',') ? pdfDataUri.split(',')[1] : pdfDataUri;
      doc.save(filename);

      // Auto-save to server
      if (this.maintenance?.id) {
        this.savingToServer = true;
        this.maintenanceService.uploadServiceCallReportPdf(this.maintenance.id, {
          fileName: filename,
          pdfBase64
        }).subscribe({
          next: () => {
            this.savingToServer = false;
            this.snackBar.open('PDF am Server gespeichert', 'OK', { duration: 2500 });
            this.dialogRef.close({
              generatedPdf: { fileName: filename, pdfBase64 }
            } as ReportDialogResult);
          },
          error: () => {
            this.savingToServer = false;
            this.snackBar.open('Server-Speicherung fehlgeschlagen – PDF nur lokal', 'OK', { duration: 3500 });
            this.dialogRef.close({
              generatedPdf: { fileName: filename, pdfBase64 }
            } as ReportDialogResult);
          }
        });
      } else {
        this.dialogRef.close({
          generatedPdf: { fileName: filename, pdfBase64 }
        } as ReportDialogResult);
      }
    } catch (e) {
      console.error('PDF generation error:', e);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // --- Signature Persistence ---

  /** Save customer signature to this service call's localStorage, and technician signature globally */
  private saveSignaturesOnExport(): void {
    const customerAcknowledged = this.isCustomerAcknowledgedForExport();
    // Save customer signature to service-call localStorage
    if (!this.isPlanned && this.maintenance?.id) {
      try {
        const key = `service-call-${this.maintenance.id}`;
        const raw = localStorage.getItem(key);
        const data = raw ? JSON.parse(raw) : {};
        if (this.custSigned && this.customerCanvas) {
          data.signatureDataUrl = this.customerCanvas.nativeElement.toDataURL('image/png');
        }
        if (this.customerSigneeName) {
          data.customerSigneeName = this.customerSigneeName;
        }
        data.regieLines = this.getRegieLinesForPersistence(customerAcknowledged);
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e) {
        console.warn('Could not save customer signature to localStorage:', e);
      }
    }

    // Save technician signature globally
    this.saveTechnicianSignature();
  }

  private persistSignaturesToMaintenance(): void {
    if (!this.maintenance?.id || this.isPlanned) return;

    const techSignatureDataUrl = this.techSigned && this.technicianCanvas
      ? this.technicianCanvas.nativeElement.toDataURL('image/png')
      : this.existingTechSignatureDataUrl;
    const customerSignatureDataUrl = this.custSigned && this.customerCanvas
      ? this.customerCanvas.nativeElement.toDataURL('image/png')
      : this.existingSignatureDataUrl;
    const customerAcknowledged = this.isCustomerAcknowledgedForExport(customerSignatureDataUrl);
    const regieLines = this.getRegieLinesForPersistence(customerAcknowledged);

    this.maintenanceService.updateMaintenance(this.maintenance.id, {
      ...this.maintenance,
      techSigneeName: this.technicianName || this.maintenance.techSigneeName || null,
      techSignatureDataUrl: techSignatureDataUrl ?? this.maintenance.techSignatureDataUrl ?? null,
      customerSigneeName: this.customerSigneeName || this.existingCustomerSigneeName || this.maintenance.customerSigneeName || null,
      customerSignatureDataUrl: customerSignatureDataUrl ?? this.maintenance.customerSignatureDataUrl ?? null,
      customerSignatureEnabled: this.includeCustomerSignature,
      customerAcknowledged,
      regieLines
    }).subscribe({
      next: (updated) => {
        this.maintenance = updated;
        this.existingTechSignatureDataUrl = updated.techSignatureDataUrl ?? this.existingTechSignatureDataUrl;
        this.existingSignatureDataUrl = updated.customerSignatureDataUrl ?? this.existingSignatureDataUrl;
      },
      error: () => {
        console.warn('Could not persist report signatures to maintenance');
      }
    });
  }

  private isCustomerAcknowledgedForExport(customerSignatureDataUrl: string | null | undefined = undefined): boolean {
    const signature = customerSignatureDataUrl === undefined
      ? (this.custSigned && this.customerCanvas ? this.customerCanvas.nativeElement.toDataURL('image/png') : this.existingSignatureDataUrl)
      : customerSignatureDataUrl;

    return this.includeCustomerSignature && (!!signature || !!this.customerSigneeName || !!this.existingCustomerSigneeName);
  }

  private getRegieLinesForPersistence(customerAcknowledged: boolean): MaintenanceRegieLine[] {
    return this.regieLines.map(line => ({
      ...line,
      isCustomerAcknowledged: customerAcknowledged ? true : !!line.isCustomerAcknowledged
    }));
  }

  /** Save technician signature with their userId as key */
  private saveTechnicianSignature(): void {
    const userId = this.maintenance?.assignedUserId;
    if (!userId || !this.techSigned || !this.technicianCanvas) return;
    try {
      const sigData = {
        name: this.technicianName,
        signatureDataUrl: this.technicianCanvas.nativeElement.toDataURL('image/png')
      };
      localStorage.setItem(`technician-sig-${userId}`, JSON.stringify(sigData));
    } catch (e) {
      console.warn('Could not save technician signature:', e);
    }
  }

  /** Load previously saved technician signature and draw on canvas */
  private loadTechnicianSignature(): void {
    const userId = this.maintenance?.assignedUserId;
    if (!userId) return;
    try {
      const raw = localStorage.getItem(`technician-sig-${userId}`);
      if (!raw) return;
      const sigData = JSON.parse(raw);
      if (sigData.signatureDataUrl && this.technicianCanvas) {
        const canvas = this.technicianCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
          this.techSigned = true;
        };
        img.src = sigData.signatureDataUrl;
      }
    } catch (e) {
      console.warn('Could not load technician signature:', e);
    }
  }

  /** Load previously saved customer signature and draw on canvas */
  private loadCustomerSignature(): void {
    if (this.isPlanned || !this.maintenance?.id) return;
    try {
      const raw = localStorage.getItem(`service-call-${this.maintenance.id}`);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.signatureDataUrl && this.customerCanvas) {
        const canvas = this.customerCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
          this.custSigned = true;
        };
        img.src = data.signatureDataUrl;
      }
      if (data.customerSigneeName && !this.customerSigneeName) {
        this.customerSigneeName = data.customerSigneeName;
      }
    } catch (e) {
      console.warn('Could not load customer signature:', e);
    }
  }

  private async resolvePhotoDataUrl(source: string): Promise<string> {
    if (!source) {
      throw new Error('Empty photo source');
    }

    if (source.startsWith('data:')) {
      return source;
    }

    const response = await fetch(source);
    if (!response.ok) {
      throw new Error('Photo could not be loaded');
    }

    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Photo conversion failed'));
      reader.readAsDataURL(blob);
    });
  }
}
